import { useCallback, useEffect, useRef, useState } from "react";

import {
  DYNAMIC_AUTOCOMPLETE_DEBOUNCE_MS,
  fetchDynamicAutocompleteSuggestions
} from "../lib/UniversalTable/dynamicAutocompleteSuggestions";
import { store } from "../redux/configureStore";
import { getAuthToken } from "../redux/modules/auth/login/selector";
import { getLocale } from "../redux/modules/translations/selector";

/**
 * Local (non-Redux) debounced suggestions for dynamic_autocomplete filters.
 */
export default function useDynamicAutocompleteSuggestions(filterDataUrl) {
  const [phrase, setPhrase] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const clear = useCallback(() => {
    requestIdRef.current += 1;
    setPhrase("");
    setSuggestions([]);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!filterDataUrl) {
      requestIdRef.current += 1;
      setSuggestions([]);
      setLoading(false);
      return undefined;
    }

    const trimmed = phrase.trim();
    if (!trimmed) {
      requestIdRef.current += 1;
      setSuggestions([]);
      setLoading(false);
      return undefined;
    }

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setSuggestions([]);
    setLoading(true);

    const timeoutId = setTimeout(async () => {
      const state = store.getState();
      const { suggestions: nextSuggestions } =
        await fetchDynamicAutocompleteSuggestions({
          filterDataUrl,
          phrase: trimmed,
          locale: getLocale(state),
          authToken: getAuthToken(state)
        });

      if (requestId !== requestIdRef.current) {
        return;
      }

      setSuggestions(nextSuggestions);
      setLoading(false);
    }, DYNAMIC_AUTOCOMPLETE_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
      requestIdRef.current += 1;
    };
  }, [filterDataUrl, phrase]);

  return {
    phrase,
    setPhrase,
    suggestions,
    loading,
    clear
  };
}
